/**
 * The Library's documents list, as the web reads it: the server answers.
 *
 * Library LB6-T2 (acceptance L6-15 .. L6-18; rails/library.md cause 4 — "the
 * server answers; the browser asks"). Before this module the Documents page
 * asked GET /knowledge/documents and GET /wiki/articles for one unfiltered
 * page each, copied both into route state, and then filtered them by folder,
 * kind, status and text, sorted them and added up its own totals in the
 * browser. A folder whose documents were past the first page looked empty, a
 * badge counted the page, and a failed read was turned into an empty list.
 *
 * Now every list on the page is ONE query here, keyed by everything it asks:
 *
 *   - the list: GET /knowledge/documents with folderId, kind, status, tagId,
 *     search, sort, sortDir, page and pageSize — the server applies THE visibility
 *     rule and every filter before LIMIT and answers `total` and
 *     `countsByKind` for the same WHERE;
 *   - "All Documents": the same route's `total` for every kind, no folder;
 *   - the reader's Previous / Next: GET /knowledge/documents/:id/neighbours
 *     with the same parameters (L6-16).
 *
 * A failed read REJECTS — the caller shows an error, never "No documents" and
 * never a 0 (principles.md #4). A response without the list is a failed read.
 * Every write that changes what a list shows calls `invalidateLibraryLists`,
 * so the table, the sidebar badges and the page trees re-ask the server in
 * the same request cycle (L6-18) — there is no route-state copy to hand-sync.
 */
import { keepPreviousData, useInfiniteQuery, useQuery, type QueryClient } from '@tanstack/react-query';
import {
  type ArticleState,
  DocumentListResponseSchema,
  DocumentNeighboursSchema,
  type DocumentKind,
  type DocumentListResponse,
  type DocumentListSort,
  type DocumentNeighbours,
  type DocumentSortDirection,
  type DocumentStatus,
  type TagListItemShape,
} from '@argusai/shared';
import { apiClient } from '@/lib/api-client';
import { invalidateLibraryFolders } from '@/lib/use-library-folders';
import { FAVORITES_QUERY_KEY } from '@/lib/favorites-store';

/** The one documents route (GET lists; /:id/neighbours names the ones either side). */
export const LIBRARY_DOCUMENTS_PATH = '/knowledge/documents';

/** Every documents-list query key starts here; invalidating it re-asks every list. */
export const libraryDocumentsQueryKey = ['knowledge', 'documents'] as const;

/** The article page trees in the sidebar and every article read start here. */
export const wikiArticlesQueryKey = ['wiki', 'articles'] as const;

/** The tag list (with the server's per-tag counts). */
export const wikiTagsQueryKey = ['wiki', 'tags'] as const;

/** The server's largest page (GET /knowledge/documents `pageSize` max). */
export const LIBRARY_DOCUMENTS_MAX_PAGE_SIZE = 100;

/** What a documents list asks the server — exactly the route's parameters. */
export interface LibraryDocumentsQuery {
  /** A folder id, `'root'` (documents at the top), or absent (every folder). */
  folderId?: string;
  /** One kind, or every kind. */
  kind: DocumentKind | 'all';
  /** One ingestion status (files and synced documents), or absent. */
  status?: DocumentStatus;
  /** One article state (articles), or absent — LB6T2-BUG-02. */
  articleState?: ArticleState;
  /** A title substring, or absent. */
  search?: string;
  /**
   * One tag's id, or absent (decision 60, LB6c-T1): documents of every kind
   * carrying it — the server's one by-tag predicate, for the list AND its
   * neighbours.
   */
  tagId?: string;
  sort: DocumentListSort;
  sortDir: DocumentSortDirection;
}

/** The query string for a list or its neighbours. `page` / `pageSize` only for a list. */
export function libraryDocumentsSearch(
  query: LibraryDocumentsQuery,
  paging?: { page: number; pageSize: number },
): string {
  const qs = new URLSearchParams();
  if (query.folderId) qs.set('folderId', query.folderId);
  qs.set('kind', query.kind);
  if (query.status) qs.set('status', query.status);
  if (query.articleState) qs.set('articleState', query.articleState);
  if (query.tagId) qs.set('tagId', query.tagId);
  const search = query.search?.trim();
  if (search) qs.set('search', search);
  qs.set('sort', query.sort);
  qs.set('sortDir', query.sortDir);
  if (paging) {
    qs.set('page', String(paging.page));
    qs.set('pageSize', String(paging.pageSize));
  }
  return qs.toString();
}

/**
 * One page of the list. REJECTS on a failed read, and on an answer that is not
 * the list's shape — never an empty list in its place.
 */
export async function fetchLibraryDocuments(
  query: LibraryDocumentsQuery,
  page: number,
  pageSize: number,
): Promise<DocumentListResponse> {
  const res = await apiClient.get<unknown>(
    `${LIBRARY_DOCUMENTS_PATH}?${libraryDocumentsSearch(query, { page, pageSize })}`,
  );
  const parsed = DocumentListResponseSchema.safeParse(res);
  if (!parsed.success) throw new Error('The document list could not be read.');
  return parsed.data;
}

/**
 * The list, page by page ("Show more" asks the server for the next page). The
 * key carries every parameter, so a change of folder, kind, status, text or
 * order is a new request — never a filter over the rows already loaded.
 */
export function useLibraryDocuments(query: LibraryDocumentsQuery, pageSize: number) {
  return useInfiniteQuery({
    queryKey: [...libraryDocumentsQueryKey, 'list', query, pageSize] as const,
    queryFn: ({ pageParam }) => fetchLibraryDocuments(query, pageParam, pageSize),
    initialPageParam: 1,
    // The next page exists while the pages read so far hold fewer rows than
    // the server's total for this query.
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.data.length, 0);
      return loaded < last.total && last.data.length > 0 ? pages.length + 1 : undefined;
    },
    staleTime: 0,
    // While the next answer is on its way the rows of the last one stay on
    // screen (no spinner flash on each keystroke); they are the server's
    // rows for the previous question, never a filter of them.
    placeholderData: keepPreviousData,
  });
}

/**
 * "All Documents": how many documents of every kind this person may see — the
 * server's `total` for the unfiltered list (the same predicate as every folder
 * badge, which is the folders response's `count`). Null while unread; a failed
 * read is the query's error, shown as no number at all.
 */
export function useLibraryDocumentTotal() {
  return useQuery<number, Error>({
    queryKey: [...libraryDocumentsQueryKey, 'total'] as const,
    queryFn: async () =>
      (await fetchLibraryDocuments({ kind: 'all', sort: 'createdAt', sortDir: 'desc' }, 1, 1)).total,
    staleTime: 0,
  });
}

/**
 * The documents before and after `documentId` in the list it was opened from
 * (L6-16) — one server answer, never a page of a list fetched to search in.
 */
export async function fetchDocumentNeighbours(
  documentId: string,
  query: LibraryDocumentsQuery,
): Promise<DocumentNeighbours> {
  const res = await apiClient.get<unknown>(
    `${LIBRARY_DOCUMENTS_PATH}/${encodeURIComponent(documentId)}/neighbours?${libraryDocumentsSearch(query)}`,
  );
  const parsed = DocumentNeighboursSchema.safeParse(res);
  if (!parsed.success) throw new Error('The neighbouring documents could not be read.');
  return parsed.data;
}

/** The neighbours as a query (the article reader's Previous / Next). */
export function useDocumentNeighbours(documentId: string, query: LibraryDocumentsQuery) {
  return useQuery<DocumentNeighbours, Error>({
    queryKey: [...libraryDocumentsQueryKey, 'neighbours', documentId, query] as const,
    queryFn: () => fetchDocumentNeighbours(documentId, query),
    staleTime: 0,
  });
}

/**
 * The tag list, each tag with how many documents this person may see carry it
 * — the server's count (GET /wiki/tags), never a tally of loaded articles.
 * REJECTS on a failed read or an answer without the list: the editor's tag
 * panel shows the error with a retry, never "no tags" and never a 0
 * (LB6T2-REV-04, principles.md #4).
 */
export async function fetchWikiTags(): Promise<TagListItemShape[]> {
  const res = await apiClient.get<{ data: TagListItemShape[] }>('/wiki/tags');
  if (!res || !Array.isArray(res.data)) throw new Error('The tag list could not be read.');
  return res.data;
}

/** The tag list as a query — one key (wikiTagsQueryKey), which every write that changes a count invalidates. */
export function useWikiTags() {
  return useQuery<TagListItemShape[], Error>({
    queryKey: wikiTagsQueryKey,
    queryFn: fetchWikiTags,
    staleTime: 0,
  });
}

/**
 * Every Library list re-asks the server — call after a document or article is
 * created, edited, moved, published, unpublished or deleted, and after a
 * folder is created, renamed or deleted (L6-18): the table, the "All
 * Documents" total, the folder badges (the folders response's counts), the
 * sidebar's page trees, the tag counts, and the person's favourites (a
 * deleted folder or document leaves the Favorites page at once, not after the
 * favourites' staleTime — LB6-T5-BF-04).
 */
export async function invalidateLibraryLists(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: libraryDocumentsQueryKey }),
    queryClient.invalidateQueries({ queryKey: wikiArticlesQueryKey }),
    invalidateLibraryFolders(queryClient),
    queryClient.invalidateQueries({ queryKey: wikiTagsQueryKey }),
    queryClient.invalidateQueries({ queryKey: FAVORITES_QUERY_KEY }),
  ]);
}
